import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { getSuperNumberHealth } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Smartphone, Loader2, RefreshCw, Wifi, WifiOff, FileText, CheckCircle2, AlertCircle } from 'lucide-react';

const errMsg = (e, fb) => e?.response?.data?.detail || fb;

// Data/hora em horário de Brasília (o backend grava em UTC).
function formatBRT(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }) + ' (Brasília)';
  } catch {
    return '—';
  }
}

// Semáforo de saúde: cor + rótulo por nível.
const LEVEL = {
  green:  { dot: 'bg-green-500', badge: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300', label: 'Muito bom' },
  yellow: { dot: 'bg-amber-500', badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300', label: 'Razoável' },
  red:    { dot: 'bg-red-500',   badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',       label: 'Ruim' },
};

// Formata número (só dígitos, ex.: "5522981302523") para "+55 22 98130-2523".
function formatPhone(digits) {
  if (!digits) return '—';
  const d = String(digits).replace(/\D/g, '');
  const m = d.match(/^55(\d{2})(\d{4,5})(\d{4})$/);
  if (m) return `+55 ${m[1]} ${m[2]}-${m[3]}`;
  return `+${d}`;
}

export default function CelularesEmUsoPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (manual) => {
    if (manual) setRefreshing(true);
    try {
      const res = await getSuperNumberHealth();
      setItems(res.data || []);
    } catch (e) {
      toast.error(errMsg(e, 'Erro ao carregar celulares em uso'));
    } finally {
      setLoading(false);
      if (manual) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(false);
    const id = setInterval(() => load(false), 60000); // atualiza a cada 60s
    return () => clearInterval(id);
  }, [load]);

  const counts = items.reduce((acc, r) => { acc[r.level] = (acc[r.level] || 0) + 1; return acc; }, {});

  return (
    <div className="flex flex-col" data-testid="celulares-em-uso-page">
      {/* Cabeçalho */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6 shrink-0">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Celulares em Uso</h1>
          <p className="text-muted-foreground">
            Saúde dos números conectados: conexão e taxa de envios aceitos nas últimas 24h.
          </p>
        </div>
        <Button variant="outline" onClick={() => load(true)} disabled={refreshing}>
          {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Atualizar
        </Button>
      </div>

      {/* Resumo do semáforo */}
      {!loading && items.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {['red', 'yellow', 'green'].map((lv) => (
            <span
              key={lv}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-sm font-medium ${LEVEL[lv].badge}`}
            >
              {LEVEL[lv].label}: <strong>{counts[lv] || 0}</strong>
            </span>
          ))}
        </div>
      )}

      <Card className="flex flex-col min-h-0 mb-4 max-h-[700px] overflow-auto">
        <CardHeader className="shrink-0">
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5" />
            Celulares em Uso
          </CardTitle>
          <CardDescription>
            Números com sessão de WhatsApp provisionada, em todos os tenants ({items.length}).
            A saúde <strong>não</strong> detecta bloqueio silencioso (mensagem aceita mas não entregue).
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0 flex flex-col min-h-0">
            <Table>
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  <TableHead>Admin</TableHead>
                  <TableHead>Número</TableHead>
                  <TableHead>Servidor</TableHead>
                  <TableHead>Conexão</TableHead>
                  <TableHead>Envios 24h</TableHead>
                  <TableHead>Saúde</TableHead>
                  <TableHead className="text-center">Aceite QR</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-6">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground text-sm">
                      Nenhum celular em uso no momento.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((r) => {
                    const lv = LEVEL[r.level] || LEVEL.green;
                    const total = (r.sent_24h || 0) + (r.failed_24h || 0);
                    return (
                      <TableRow key={r.tenant_id} className="hover:bg-primary/5">
                        <TableCell className="py-2">
                          <div className="font-medium">{r.admin_name}</div>
                          <div className="text-xs text-muted-foreground">{r.email}</div>
                        </TableCell>
                        <TableCell className="py-2 text-sm">{formatPhone(r.number)}</TableCell>
                        <TableCell className="py-2 text-sm">{r.server_name}</TableCell>
                        <TableCell className="py-2">
                          {r.connected ? (
                            <Badge variant="outline" className="gap-1 border-transparent shadow bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                              <Wifi className="w-3 h-3" /> Conectado
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="gap-1 border-transparent shadow bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
                              <WifiOff className="w-3 h-3" /> Desconectado
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="py-2 text-sm">
                          {total === 0 ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <span>
                              {r.sent_24h} aceito(s)
                              {r.failed_24h > 0 ? `, ${r.failed_24h} falha(s)` : ''}
                              {r.success_rate != null ? ` · ${Math.round(r.success_rate * 100)}%` : ''}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="py-2">
                          <Badge variant="outline" className={`gap-1.5 border-transparent shadow ${lv.badge}`} title={r.reason}>
                            <span className={`inline-block h-2 w-2 rounded-full ${lv.dot}`} />
                            {lv.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-2 text-center">
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                title="Ver registro do aceite do aviso de conexão"
                                className="inline-flex items-center justify-center rounded-md p-1.5 text-foreground hover:bg-primary/10 transition-colors"
                              >
                                <FileText className="w-4 h-4" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent align="end" sideOffset={6} className="w-72 p-3 text-sm">
                              <div className="flex items-center gap-2 font-medium mb-2">
                                <FileText className="w-4 h-4 text-foreground" />
                                Aceite do aviso de conexão
                              </div>
                              {r.ack?.acked ? (
                                <div className="space-y-1.5">
                                  <div className="flex items-center gap-1.5 text-green-700 dark:text-green-400 font-medium">
                                    <CheckCircle2 className="w-4 h-4" /> Confirmado
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground">Quem: </span>
                                    {r.ack.by_email || '—'}
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground">Quando: </span>
                                    {formatBRT(r.ack.at)}
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground">Versão do aviso: </span>
                                    {r.ack.version || '—'}
                                  </div>
                                  {!r.ack.current && (
                                    <div className="flex items-start gap-1.5 text-amber-700 dark:text-amber-400 text-xs mt-1">
                                      <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                      Confirmado numa versão anterior do aviso.
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="space-y-1.5">
                                  <div className="flex items-center gap-1.5 text-amber-700 dark:text-amber-400 font-medium">
                                    <AlertCircle className="w-4 h-4" /> Sem registro
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    Este número foi conectado antes de o aviso passar a exigir a
                                    confirmação, ou o aceite ainda não foi dado.
                                  </p>
                                </div>
                              )}
                            </PopoverContent>
                          </Popover>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
        </CardContent>
      </Card>
    </div>
  );
}
